import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PWAPrompt } from "@/components/PWAPrompt";
import { RealtimeSync } from "@/components/RealtimeSync";
import ScrollToTop from "@/components/ScrollToTop";
import { GDBErrorBoundary } from "@/components/GDBErrorBoundary";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";

import { useDataPreloader } from "@/hooks/useDataPreloader";
import Index from "./pages/Index";
import Home from "./pages/Home";
import Social from "./pages/Social";
import Ambiental from "./pages/Ambiental";
import SST from "./pages/SST";
import Herramientas from "./pages/Herramientas";
import DocumentosSociales from "./pages/DocumentosSociales";
import GestorAmbiental from "./pages/GestorAmbiental";
import GestorSocial from "./pages/GestorSocial";
import GestorSST from "./pages/GestorSST";
import ReporteIntegrado from "./pages/ReporteIntegrado";
import ReporteAmbiental from "./pages/ReporteAmbiental";
import ReporteAmbientalDetalle from "./pages/ReporteAmbientalDetalle";
import ReporteSST from "./pages/ReporteSST";
import ReporteSSTDetalle from "./pages/ReporteSSTDetalle";
import ReporteSocial from "./pages/ReporteSocial";
import ReporteSocialDetalle from "./pages/ReporteSocialDetalle";
import Legal from "./pages/Legal";
import GestorGDB from "./pages/GestorGDB";
import Configuraciones from "./pages/Configuraciones";
import GestionUsuarios from "./pages/GestionUsuarios";
import ConfiguracionRoles from "./pages/ConfiguracionRoles";
import ConfiguracionBaseDatos from "./pages/ConfiguracionBaseDatos";
import ConfiguracionNotificaciones from "./pages/ConfiguracionNotificaciones";
import ManualMarca from "./pages/ManualMarca";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AppContent() {
  useDataPreloader();

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/social" element={<ProtectedRoute><Social /></ProtectedRoute>} />
        <Route path="/ambiental" element={<ProtectedRoute><Ambiental /></ProtectedRoute>} />
        <Route path="/sst" element={<ProtectedRoute><SST /></ProtectedRoute>} />
        <Route path="/herramientas" element={<ProtectedRoute><Herramientas /></ProtectedRoute>} />
        <Route path="/documentos-sociales" element={<ProtectedRoute><DocumentosSociales /></ProtectedRoute>} />
        <Route path="/gestor-ambiental" element={<ProtectedRoute><GestorAmbiental /></ProtectedRoute>} />
        <Route path="/gestor-social" element={<ProtectedRoute><GestorSocial /></ProtectedRoute>} />
        <Route path="/gestor-sst" element={<ProtectedRoute><GestorSST /></ProtectedRoute>} />
        <Route path="/reporte-integrado" element={<ProtectedRoute><ReporteIntegrado /></ProtectedRoute>} />
        <Route path="/reporte-ambiental" element={<ProtectedRoute><ReporteAmbiental /></ProtectedRoute>} />
        <Route path="/reporte-ambiental/:id" element={<ProtectedRoute><ReporteAmbientalDetalle /></ProtectedRoute>} />
        <Route path="/reporte-sst" element={<ProtectedRoute><ReporteSST /></ProtectedRoute>} />
        <Route path="/reporte-sst/:id" element={<ProtectedRoute><ReporteSSTDetalle /></ProtectedRoute>} />
        <Route path="/reporte-social" element={<ProtectedRoute><ReporteSocial /></ProtectedRoute>} />
        <Route path="/reporte-social/:id" element={<ProtectedRoute><ReporteSocialDetalle /></ProtectedRoute>} />
        <Route path="/legal" element={<ProtectedRoute><Legal /></ProtectedRoute>} />
        <Route path="/gestor-gdb" element={<ProtectedRoute><GDBErrorBoundary><GestorGDB /></GDBErrorBoundary></ProtectedRoute>} />
        <Route path="/configuraciones" element={<ProtectedRoute><Configuraciones /></ProtectedRoute>} />
        <Route path="/gestion-usuarios" element={<ProtectedRoute><GestionUsuarios /></ProtectedRoute>} />
        <Route path="/configuracion-roles" element={<ProtectedRoute><ConfiguracionRoles /></ProtectedRoute>} />
        <Route path="/configuracion-base-datos" element={<ProtectedRoute><ConfiguracionBaseDatos /></ProtectedRoute>} />
        <Route path="/configuracion-notificaciones" element={<ProtectedRoute><ConfiguracionNotificaciones /></ProtectedRoute>} />
        <Route path="/manual-marca" element={<ProtectedRoute><ManualMarca /></ProtectedRoute>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <PWAPrompt />
      <RealtimeSync />
    </>
  );
}

function App() {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}

export default App;
